"use client";

import React from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, ShieldCheck, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Clapham?",
  answer: "Yes. We provide scaffolding services across Clapham, SW4, and surrounding South West London locations, catering to both residential properties and commercial developments."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Clapham?",
  answer: "We supply independent access scaffolds, temporary roof structures, edge protection, chimney stacks, birdcage scaffolding, and dedicated access platforms for solar panel installations and refurbishments."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We regularly erect perimeter handrails, chimney scaffolds, loading bays, and fully sheeted temporary roofs to keep roofing works safe and weather protected."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We partner with main contractors, roofing contractors, solar installers, property managers, and commercial developers, providing full handovers, safety documentation, and ongoing statutory inspections."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Clapham properties often feature narrow side returns, basement lightwells, or tight boundaries. We design tailored tube-and-fitting configurations, cantilevered sections, or bridge beams to maintain access and safety."
}, {
  id: "item-6",
  question: "How do I request a quote in Clapham?",
  answer: "You can send your project details, site address, and any available architectural drawings or photos through our online contact form, or contact our team directly to arrange a site visit."
}];
export default function FaqClapham() {
  return <section id="faqclapham" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20 bg-primary/5">
            <HelpCircle className="size-3.5 text-primary" />
            Clapham Access FAQ
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold font-default tracking-tight text-foreground">
            Frequently asked questions about scaffolding in Clapham
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            Clear, factual answers regarding our scaffolding capabilities, site
            safety, and access solutions across Clapham.
          </p>
        </motion.div>

        {/* Accordion Container */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="bg-card text-card-foreground border border-border rounded-xl p-4 sm:p-8 shadow-sm">
          <Accordion type="single" collapsible className="w-full space-y-2">
            {faqItems.map((item, i) => <div key={item.id} data-index={i}>
                <AccordionItem value={item.id} className="border-b border-border/80 px-2 py-1 last:border-b-0">
                  <AccordionTrigger className="text-base sm:text-lg font-medium text-foreground hover:no-underline hover:text-primary transition-colors py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        {/* Bottom Support & CTA Box */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-10 rounded-xl bg-accent/40 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="hidden sm:flex size-12 rounded-lg bg-primary/10 text-primary items-center justify-center shrink-0">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                Need specific access arrangements?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Speak directly with our team to discuss site surveys and safety plans.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto justify-center">
            <Button asChild variant="default" className="gap-2">
              <Link to="/ContactRequestQuotePage">
                <span>Request a quote</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/AreasWeCoverPage">
                <span>View service areas</span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}